import { useState } from 'react';
import { Collapse } from '@mui/material';
import { Chip, Button } from '@coordio/ui';
import { ChevronDown as ExpandMoreIcon, ChevronUp as ExpandLessIcon, Wrench as BuildIcon, CheckCircle as CheckIcon, CircleX as ErrorIcon } from 'lucide-react';

interface ToolCallCardProps {
  toolName: string;
  args: string;
  result?: string;
  isSuccess?: boolean;
  isRunning?: boolean;
}

export const ToolCallCard = ({ toolName, args, result, isSuccess, isRunning }: ToolCallCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const statusIcon = isRunning
    ? <span className="inline-flex animate-spin text-blue-500"><BuildIcon size={16} /></span>
    : isSuccess === true
      ? <CheckIcon size={16} className="text-green-600" />
      : isSuccess === false
        ? <ErrorIcon size={16} className="text-red-600" />
        : null;

  return (
    <div
      className="my-1 rounded border border-border bg-muted"
    >
      <div
        className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-muted/80"
        onClick={() => setExpanded((v) => !v)}
      >
        {statusIcon}
        <Chip label={toolName} variant="outlined" color="default" />
        <div className="flex-1" />
        <Button variant="ghost" size="icon" className="h-6 w-6">
          {expanded ? <ExpandLessIcon size={16} /> : <ExpandMoreIcon size={16} />}
        </Button>
      </div>
      <Collapse in={expanded}>
        <div className="px-3 pb-3">
          <span className="text-xs font-semibold text-muted-foreground">Arguments</span>
          <pre
            className="m-0 mt-1 max-h-[200px] overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 font-mono text-[11px]"
          >
            {formatJson(args)}
          </pre>
          {result !== undefined && (
            <>
              <span className="mt-2 block text-xs font-semibold text-muted-foreground">
                Result
              </span>
              <pre
                className="m-0 mt-1 max-h-[200px] overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 font-mono text-[11px]"
              >
                {result}
              </pre>
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};

function formatJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
